import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import styles from './FeedbackPage.module.css';

interface FeedbackEntry {
  id: number;
  feedback_type: 'general' | 'context_dependent';
  feedback_text: string;
  category: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  is_active: boolean;
  created_at: string;
}

const CATEGORIES = ['tone', 'accuracy', 'clarity', 'formatting', 'content', 'other'];
const SENTIMENTS = ['positive', 'negative', 'neutral'];

export function FeedbackPage() {
  const { isEditor, isAdmin } = useAuth();
  const [feedbackEntries, setFeedbackEntries] = useState<FeedbackEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | null>(true);

  // Form state
  const [formData, setFormData] = useState({
    feedback_type: 'general' as 'general' | 'context_dependent',
    feedback_text: '',
    category: 'content',
    sentiment: 'neutral' as 'positive' | 'negative' | 'neutral',
  });

  useEffect(() => {
    loadFeedback();
  }, [filterActive]);

  const loadFeedback = async () => {
    setIsLoading(true);
    try {
      let url = '/feedback';
      if (filterActive !== null) {
        url += `?is_active=${filterActive}`;
      }
      const response = await api.get<FeedbackEntry[]>(url);
      if (response.success && response.data) {
        setFeedbackEntries(response.data);
      }
    } catch (err) {
      setError('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await api.post('/feedback', formData);
      if (response.success) {
        await loadFeedback();
        setShowForm(false);
        setFormData({
          feedback_type: 'general',
          feedback_text: '',
          category: 'content',
          sentiment: 'neutral',
        });
      } else {
        setError(response.error || 'Failed to create feedback');
      }
    } catch (err) {
      setError('Failed to create feedback');
    }
  };

  const handleToggleActive = async (id: number) => {
    try {
      const response = await api.post(`/feedback/${id}/toggle`);
      if (response.success) {
        await loadFeedback();
      } else {
        setError(response.error || 'Failed to toggle feedback');
      }
    } catch (err) {
      setError('Failed to toggle feedback');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return;

    try {
      const response = await api.delete(`/feedback/${id}`);
      if (response.success) {
        setFeedbackEntries((prev) => prev.filter((f) => f.id !== id));
      } else {
        setError(response.error || 'Failed to delete feedback');
      }
    } catch (err) {
      setError('Failed to delete feedback');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getSentimentBadge = (sentiment: FeedbackEntry['sentiment']) => {
    const map = {
      positive: 'success',
      negative: 'error',
      neutral: 'secondary',
    };
    return `badge badge-${map[sentiment]}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Feedback</h1>
          <p className={styles.subtitle}>
            Learning feedback for commentary generation
          </p>
        </div>
        {isEditor && (
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add Feedback'}
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Add Feedback Form */}
      {showForm && (
        <div className={styles.form}>
          <h2>Add New Feedback</h2>
          <form onSubmit={handleSubmit}>
            <div className={styles.formRow}>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={formData.feedback_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      feedback_type: e.target.value as 'general' | 'context_dependent',
                    })
                  }
                >
                  <option value="general">General</option>
                  <option value="context_dependent">Context Dependent</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Sentiment</label>
                <select
                  className="form-select"
                  value={formData.sentiment}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sentiment: e.target.value as 'positive' | 'negative' | 'neutral',
                    })
                  }
                >
                  {SENTIMENTS.map((sent) => (
                    <option key={sent} value={sent}>
                      {sent.charAt(0).toUpperCase() + sent.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Feedback</label>
              <textarea
                className="form-textarea"
                value={formData.feedback_text}
                onChange={(e) =>
                  setFormData({ ...formData, feedback_text: e.target.value })
                }
                placeholder="Enter your feedback..."
                rows={4}
                required
              />
            </div>

            <button type="submit" className="btn btn-primary">
              Submit Feedback
            </button>
          </form>
        </div>
      )}

      {/* Filter */}
      <div className={styles.filter}>
        <span>Show:</span>
        <button
          className={`${styles.filterBtn} ${filterActive === true ? styles.filterBtnActive : ''}`}
          onClick={() => setFilterActive(true)}
        >
          Active
        </button>
        <button
          className={`${styles.filterBtn} ${filterActive === false ? styles.filterBtnActive : ''}`}
          onClick={() => setFilterActive(false)}
        >
          Inactive
        </button>
        <button
          className={`${styles.filterBtn} ${filterActive === null ? styles.filterBtnActive : ''}`}
          onClick={() => setFilterActive(null)}
        >
          All
        </button>
      </div>

      {/* Feedback List */}
      {isLoading ? (
        <div className={styles.loading}>
          <div className="spinner" />
          <span>Loading feedback...</span>
        </div>
      ) : feedbackEntries.length === 0 ? (
        <div className={styles.empty}>
          <p>No feedback entries found</p>
        </div>
      ) : (
        <div className={styles.list}>
          {feedbackEntries.map((entry) => (
            <div
              key={entry.id}
              className={`${styles.card} ${!entry.is_active ? styles.cardInactive : ''}`}
            >
              <div className={styles.cardHeader}>
                <div className={styles.cardMeta}>
                  <span className="badge badge-info">{entry.feedback_type}</span>
                  <span className="badge badge-secondary">{entry.category}</span>
                  <span className={getSentimentBadge(entry.sentiment)}>
                    {entry.sentiment}
                  </span>
                </div>
                <span className={styles.cardDate}>{formatDate(entry.created_at)}</span>
              </div>
              <p className={styles.cardText}>{entry.feedback_text}</p>
              {isEditor && (
                <div className={styles.cardActions}>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handleToggleActive(entry.id)}
                  >
                    {entry.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  {isAdmin && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(entry.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
